import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Plus, Trash2, Clock, ChevronLeft } from 'lucide-react';
import api from '../../utils/api';
import { useToast } from '../../components/Toast';
import TabBar from '../../components/TabBar';
import AdminNotificationBell from '../../components/AdminNotificationBell';

export default function DailySummaryPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [times, setTimes] = useState([]); // ["15:00","21:00"]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/settings/daily-summary')
      .then(res => setTimes(res.data.times || []))
      .catch(() => showToast('加载失败', 'error'))
      .finally(() => setLoading(false));
  }, [showToast]);

  // 新增一个时间（默认 08:00，避开常用的两个）
  const addTime = () => {
    if (times.length >= 3) { showToast('最多配置 3 个时间点', 'error'); return; }
    const defaults = ['08:00', '15:00', '21:00'];
    const next = defaults.find(t => !times.includes(t)) || '12:00';
    setTimes(prev => [...prev, next].sort());
  };

  const removeTime = (idx) => {
    setTimes(prev => prev.filter((_, i) => i !== idx));
  };

  const updateTime = (idx, val) => {
    setTimes(prev => {
      const next = [...prev];
      next[idx] = val;
      return [...new Set(next)].sort(); // 去重+排序
    });
  };

  const handleSave = async () => {
    // 校验
    for (const t of times) {
      const [h, m] = t.split(':').map(Number);
      if (isNaN(h) || isNaN(m) || h > 23 || m > 59) {
        showToast(`时间格式有误：${t}`, 'error'); return;
      }
    }
    setSaving(true);
    try {
      const res = await api.put('/admin/settings/daily-summary', { times });
      setTimes(res.data.times || []);
      showToast(res.data.message || '已保存', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || '保存失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          onClick={() => navigate('/admin')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px 4px 0', color: 'var(--c-primary)', display: 'flex', alignItems: 'center' }}
        >
          <ChevronLeft size={20} />
        </button>
        <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Bell size={16} /> 每日汇总推送
        </span>
        <AdminNotificationBell />
      </div>

      <div style={{ padding: '16px 16px 100px' }}>

        {/* 说明卡片 */}
        <div className="card" style={{ padding: '12px 14px', marginBottom: 16, background: 'var(--c-primary-light)' }}>
          <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', lineHeight: 1.7 }}>
            每到配置的时间点，系统自动推送当日汇总：
            <br />
            · <b>管理员</b>收到全站汇总（含管理员、代理本人提交的），<b>代理</b>收到本人 + 名下汇总（代理显示“名下截止…”）
            <br />
            · 格式示例：
            <div style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.7, whiteSpace: 'pre-wrap', background: 'var(--c-bg)', borderRadius: 6, padding: '8px 10px', margin: '6px 0' }}>
{`全站截止15:00：
排五:成功 80 组 / 重复 3 组 / 无效 2 组 / 失败 1 张
证券:成功 45 单 / 待核实 5 单`}
            </div>
            · 哪个业务今天有数据就显示哪一行；排五、证券都没有数据时，整条汇总不推送（管理员和代理一样）
            <br />
            · 证券的“成功”“待核实”都只统计当天（北京时间自然日）提交的
          </div>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', padding: 40, color: 'var(--c-text-secondary)' }}>加载中…</p>
        ) : (
          <>
            {/* 时间列表 */}
            {times.length === 0 ? (
              <div className="card" style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--c-text-secondary)', marginBottom: 12 }}>
                <Clock size={32} strokeWidth={1.2} style={{ marginBottom: 8, opacity: 0.4 }} />
                <p style={{ fontSize: 13, margin: 0 }}>尚未配置任何时间点<br />点击下方按钮添加</p>
              </div>
            ) : (
              <div style={{ marginBottom: 12 }}>
                {times.map((t, idx) => (
                  <div key={idx} className="card" style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 14px', marginBottom: 8,
                  }}>
                    <Clock size={16} color="var(--c-primary)" style={{ flexShrink: 0 }} />
                    <input
                      type="time"
                      value={t}
                      onChange={e => updateTime(idx, e.target.value)}
                      style={{
                        flex: 1,
                        fontSize: 20,
                        fontWeight: 700,
                        fontFamily: 'monospace',
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--c-text)',
                        outline: 'none',
                        cursor: 'pointer',
                        minWidth: 0,
                      }}
                    />
                    <span style={{ fontSize: 12, color: 'var(--c-text-secondary)', flexShrink: 0 }}>
                      每日推送
                    </span>
                    <button
                      onClick={() => removeTime(idx)}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--c-danger)', display: 'flex', alignItems: 'center',
                        padding: 4, flexShrink: 0,
                      }}
                      title="删除此时间点"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* 添加按钮 */}
            {times.length < 3 && (
              <button
                className="btn btn-outline"
                onClick={addTime}
                style={{
                  width: '100%', height: 44, marginBottom: 16,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                <Plus size={16} /> 添加时间点（最多 3 个）
              </button>
            )}

            {/* 保存 */}
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
              style={{ width: '100%', height: 48, fontSize: 15 }}
            >
              {saving ? '保存中…' : '保存配置'}
            </button>

            {/* 关闭提示 */}
            {times.length > 0 && (
              <p style={{ fontSize: 12, color: 'var(--c-text-secondary)', textAlign: 'center', marginTop: 12 }}>
                删除全部时间点后保存即可关闭定时推送
              </p>
            )}
          </>
        )}
      </div>

      <TabBar mode="admin" />
    </div>
  );
}
